// communicatie: the {Group.Field} insertable-variable catalog. The AUTHORITATIVE source is now the
// server endpoint GET communicatie/templates/fields (service/communicatie/TemplateFieldCatalog.java) —
// VariablePalette fetches it at runtime so the palette can never drift from the publish-time validator.
// This STATIC list is kept only as an OFFLINE FALLBACK: if the fetch fails (network / not loaded yet),
// the palette renders these instead of nothing. The publish-time validation still runs server-side
// against the authoritative Java catalog (unknown tokens → 422).
//
// token = the literal placeholder text (without braces); the pill renders as {token} and serializes back
// to {token} verbatim. label = the human-friendly name; group = palette grouping; description = hint.

/** The grouped shape returned by GET communicatie/templates/fields. */
export interface ServerFieldGroup {
  group: string;
  // `description` is the per-field tooltip (Jmix TemplateField parity); optional so an older server that
  // doesn't send it still parses (the palette then renders the token-only tooltip).
  fields: { token: string; label: string; description?: string }[];
}
export interface ServerFieldCatalog {
  groups: ServerFieldGroup[];
}

/** Normalize the server catalog into the palette's display shape (token/label/description, group preserved). */
export function fieldsFromServer(catalog: ServerFieldCatalog | null | undefined): {
  group: string;
  fields: TemplateField[];
}[] {
  if (!catalog?.groups?.length) return [];
  return catalog.groups.map((g) => ({
    group: g.group,
    fields: g.fields.map((f) => ({
      token: f.token,
      group: g.group,
      label: f.label,
      description: f.description ?? "",
    })),
  }));
}

export interface TemplateField {
  token: string;
  group: string;
  label: string;
  description: string;
}

export const TEMPLATE_FIELDS: TemplateField[] = [
  // Relatie
  { token: "Relatie.Aanhef", group: "Relatie", label: "Aanhef", description: "Bv. 'Geachte heer' of 'Fam. van Diessen'" },
  { token: "Relatie.Naam", group: "Relatie", label: "Naam", description: "Naam van de relatie" },
  { token: "Relatie.VolledigeNaam", group: "Relatie", label: "Volledige naam", description: "Volledige tenaamstelling" },
  { token: "Relatie.Voorletters", group: "Relatie", label: "Voorletters", description: "Voorletters van de relatie" },
  { token: "Relatie.Achternaam", group: "Relatie", label: "Achternaam", description: "Achternaam (incl. tussenvoegsel)" },
  { token: "Relatie.Adres", group: "Relatie", label: "Adres", description: "Straat + huisnummer" },
  { token: "Relatie.Postcode", group: "Relatie", label: "Postcode", description: "Postcode van de relatie" },
  { token: "Relatie.Woonplaats", group: "Relatie", label: "Woonplaats", description: "Woonplaats van de relatie" },
  { token: "Relatie.Email", group: "Relatie", label: "E-mail", description: "E-mailadres van de relatie" },
  { token: "Relatie.Telefoon", group: "Relatie", label: "Telefoon", description: "Telefoonnummer van de relatie" },
  { token: "Relatie.Mobiel", group: "Relatie", label: "Mobiel", description: "Mobiel nummer van de relatie" },
  { token: "Relatie.Legitimatiesoort", group: "Relatie", label: "Legitimatiesoort", description: "Soort legitimatiebewijs" },
  { token: "Relatie.Legitimatienummer", group: "Relatie", label: "Legitimatienummer", description: "Nummer van het legitimatiebewijs" },
  // Dier
  { token: "Dier.Naam", group: "Dier", label: "Diernaam", description: "Naam van het dier" },
  { token: "Dier.Registratienummer", group: "Dier", label: "Registratienummer", description: "Asiel-/registratienummer van het dier" },
  { token: "Dier.Soort", group: "Dier", label: "Diersoort", description: "Soort, bv. hond of kat" },
  { token: "Dier.Ras", group: "Dier", label: "Ras", description: "Ras van het dier" },
  { token: "Dier.Kruising", group: "Dier", label: "Kruising", description: "Of het dier een kruising is" },
  { token: "Dier.Geslacht", group: "Dier", label: "Geslacht", description: "Geslacht van het dier" },
  { token: "Dier.Gecastreerd", group: "Dier", label: "Geneutraliseerd", description: "Gecastreerd/gesteriliseerd (ja/nee)" },
  { token: "Dier.Kleur", group: "Dier", label: "Kleur", description: "Kleur van het dier" },
  { token: "Dier.Vacht", group: "Dier", label: "Vacht", description: "Vachttype van het dier" },
  { token: "Dier.Chipnummer", group: "Dier", label: "Chipnummer", description: "Chip-/transpondernummer" },
  { token: "Dier.Geboortedatum", group: "Dier", label: "Geboortedatum", description: "Geboortedatum van het dier" },
  { token: "Dier.Leeftijd", group: "Dier", label: "Leeftijd", description: "Leeftijd van het dier" },
  { token: "Dier.Bijzonderheden", group: "Dier", label: "Bijzonderheden", description: "Medische/gedrags-bijzonderheden van het dier" },
  // Verblijf
  { token: "Verblijf.Startdatum", group: "Verblijf", label: "Startdatum", description: "Aankomst-/startdatum" },
  { token: "Verblijf.Einddatum", group: "Verblijf", label: "Einddatum", description: "Vertrek-/einddatum" },
  { token: "Verblijf.Type", group: "Verblijf", label: "Verblijftype", description: "Type verblijf, bv. pension" },
  { token: "Verblijf.Locatie", group: "Verblijf", label: "Locatie", description: "Kennel/locatie van het verblijf" },
  { token: "Verblijf.Opnamedatum", group: "Verblijf", label: "Opnamedatum", description: "Datum van opname" },
  { token: "Verblijf.Vertrekdatum", group: "Verblijf", label: "Vertrekdatum", description: "Datum van vertrek/plaatsing" },
  { token: "Verblijf.RedenAfstand", group: "Verblijf", label: "Reden afstand", description: "Reden voor afstand/opname" },
  { token: "Verblijf.Vindplaats", group: "Verblijf", label: "Vindplaats", description: "Gemeente/plaats waar gevonden" },
  { token: "Verblijf.OpnameSoort", group: "Verblijf", label: "Opnamesoort", description: "Soort opname, bv. zwerver/afstand" },
  { token: "Verblijf.Bijzonderheden", group: "Verblijf", label: "Bijzonderheden", description: "Bijzonderheden over het verblijf/dier" },
  // Factuur
  { token: "Factuur.Nummer", group: "Factuur", label: "Factuurnummer", description: "Nummer van de factuur" },
  { token: "Factuur.Bedrag", group: "Factuur", label: "Bedrag", description: "Openstaand/totaalbedrag" },
  { token: "Factuur.Vervaldatum", group: "Factuur", label: "Vervaldatum", description: "Vervaldatum van de factuur" },
  { token: "Factuur.Betaallink", group: "Factuur", label: "Betaallink", description: "Online betaallink (genereert QR indien gebruikt)" },
  { token: "Factuur.Opmerking", group: "Factuur", label: "Opmerking", description: "De opmerking op de factuur; leeg als er geen is" },
  // Kosten / factuurregels
  { token: "Kostenregel.Product", group: "Kosten", label: "Productregel", description: "Omschrijving van de kostenregel (in herhaalblok Kostenregel)" },
  { token: "Kostenregel.Bedrag", group: "Kosten", label: "Regelbedrag", description: "Bedrag van de kostenregel (in herhaalblok Kostenregel)" },
  { token: "Kostenregel.Korting", group: "Kosten", label: "Regelkorting", description: "Korting op de kostenregel, leeg als er geen is (in herhaalblok Kostenregel)" },
  { token: "Kosten.Totaal", group: "Kosten", label: "Totaalbedrag", description: "Totaal van alle kostenregels" },
  // Vestiging
  { token: "Vestiging.Naam", group: "Vestiging", label: "Vestigingsnaam", description: "Naam van de vestiging" },
  { token: "Vestiging.Afkorting", group: "Vestiging", label: "Afkorting", description: "Afkorting/roepnaam van de vestiging" },
  { token: "Vestiging.Adres", group: "Vestiging", label: "Vestigingsadres", description: "Adres van de vestiging" },
  { token: "Vestiging.Postcode", group: "Vestiging", label: "Vestiging postcode", description: "Postcode van de vestiging" },
  { token: "Vestiging.Woonplaats", group: "Vestiging", label: "Vestiging woonplaats", description: "Woonplaats van de vestiging" },
  { token: "Vestiging.Telefoon", group: "Vestiging", label: "Vestiging telefoon", description: "Telefoonnummer van de vestiging" },
  { token: "Vestiging.Email", group: "Vestiging", label: "Vestiging e-mail", description: "E-mailadres van de vestiging" },
  // Briefpapier: het briefhoofd/de voettekst van de afdrukken komt per installatie uit de vestiging.
  { token: "Vestiging.Website", group: "Vestiging", label: "Vestiging website", description: "Website van de vestiging" },
  { token: "Vestiging.Kvk", group: "Vestiging", label: "KvK-nummer", description: "Kamer van Koophandel-nummer van de vestiging" },
  { token: "Vestiging.Btw", group: "Vestiging", label: "BTW-nummer", description: "BTW-nummer van de vestiging" },
  { token: "Vestiging.Iban", group: "Vestiging", label: "IBAN", description: "Bankrekeningnummer (IBAN) van de vestiging" },
  { token: "Vestiging.Logo", group: "Vestiging", label: "Logo", description: "Logo van de vestiging (als afbeelding in het briefhoofd)" },
  // Datum
  { token: "Datum.Vandaag", group: "Datum", label: "Datum vandaag", description: "Huidige datum bij verzenden/afdrukken" },
];

/** Fields grouped by `group`, declaration order preserved (matches Java fieldsByGroup()). */
export function fieldsByGroup(): { group: string; fields: TemplateField[] }[] {
  const order: string[] = [];
  const map = new Map<string, TemplateField[]>();
  for (const f of TEMPLATE_FIELDS) {
    if (!map.has(f.group)) {
      map.set(f.group, []);
      order.push(f.group);
    }
    map.get(f.group)!.push(f);
  }
  return order.map((group) => ({ group, fields: map.get(group)! }));
}
